import axios from "axios";
import { getAuthHeaders } from "../../../../utils/authUtils";
import { API_BASE_URL, MIDDLEWARE_BASE_URL } from "../../../../environment";

export interface CalendarEventType {
  calendar_event_type_id: number;
  calendar_event_type_key: string;
  calendar_event_type_display_name: string;
  calendar_provider: {
    calendar_provider_id: number;
    calendar_provider_key: string;
    calendar_provider_display_name: string;
  };
}

export interface LinkedCalendar {
  calendar_auth_linked_calendar_id: number;
  calendar_id: string;
  name: string;
  description: string;
  timezone: string;
  is_primary: boolean;
  last_synced_at: string;
  raw_data: string;
  created_datetime: string;
  updated_datetime: string;
  calendar_auth_id: number;
  created_by: { user_id: number; first_name: string; last_name: string; email: string };
  updated_by: { user_id: number; first_name: string; last_name: string; email: string };
  tenant: { tenant_id: number; tenant_name: string; domain_name: string };
  calendar_color_category: {
    calendar_color_category_id: number;
    background_color_code: string;
    foreground_color_code: string;
    color_type: string;
  };
  calendar_auth: {
    calendar_auth_id: number;
    external_auth_user_id: string;
    external_auth_user_email: string;
    external_auth_user_name: string;
    is_authorized: boolean;
  };
  calendar_provider: {
    calendar_provider_id: number;
    calendar_provider_key: string;
    calendar_provider_display_name: string;
  };
}

export interface EventAttachment {
  attachment_id: number;
  title: string;
  size_in_kb: number;
  attachment_url: string;
  extension: string;
}

export interface CalendarEvent {
  calendar_event_id: number;
  title: string;
  description: string;
  location: string;
  start_datetime: string;
  end_datetime: string;
  is_all_day: boolean;
  timezone: string;
  status: string;
  last_synced_at: string;
  recurrence_rule: string | null;
  recurrence_end_datetime: string | null;
  master_event_id: string;
  visibility: string;
  created_datetime: string;
  updated_datetime: string;
  html_link: string;
  event_id: string;
  tenant_id: number;
  calendar_auth_linked_calendar_id: number;
  calendar_event_type_id: number;
  calendar_color_category_id: number;
  created_by: { user_id: number; first_name: string; last_name: string; email: string };
  updated_by: { user_id: number; first_name: string; last_name: string; email: string };
  tenant: { tenant_id: number; tenant_name: string; domain_name: string };
  calendar_event_type: {
    calendar_event_type_id: number;
    calendar_event_type_key: string;
    calendar_event_type_display_name: string;
  };
  calendar_auth_linked_calendar: {
    calendar_auth_linked_calendar_id: number;
    name: string;
    calendar_id: string;
    description: string;
    timezone: string;
    is_primary: boolean;
    last_synced_at: string;
  };
  calendar_color_category: {
    calendar_color_category_id: number;
    background_color_code: string;
    foreground_color_code: string;
    color_type: string;
  };
  is_recursive: boolean;
  attachments: EventAttachment[];
  related_entity_id: number | null;
  related_entity_name: string | null;
}

export interface CalendarTaskGroup {
  calendar_task_group_id: number;
  external_task_group_id: string;
  task_group_name: string;
  created_datetime?: string;
  updated_datetime?: string;
  tenant_id?: number;
  calendar_auth_id?: number;
  calendar_provider_id?: number;
}

export interface CalendarTask {
  calendar_task_id: number;
  title: string;
  notes: string;
  due_datetime: string | null;
  status: string;
  is_completed: boolean;
  priority: string;
  parent_task_id: string | null;
  recurrence_rule: string | null;
  recurrence_end_datetime: string | null;
  completed_datetime: string | null;
  created_datetime: string;
  updated_datetime: string;
  external_task_id: string;
  task_source: string;
  is_deleted: boolean;
  tenant_id: number;
  calendar_task_group_id: number;
  created_by: { user_id: number; first_name: string; last_name: string; email: string };
  updated_by: { user_id: number; first_name: string; last_name: string; email: string };
  calendar_auth_id: number;
  related_task_id: number | null;
  tenant: { tenant_id: number; tenant_name: string; domain_name: string };
  calendar_task_group: CalendarTaskGroup;
  calendar_auth: {
    calendar_auth_id: number;
    external_auth_user_id: string;
    external_auth_user_email: string;
    external_auth_user_name: string;
    is_authorized: boolean;
  };
  sub_tasks?: CalendarTask[];
}

export interface EventMeeting {
  calendar_event_meeting_id: number;
  meeting_provider: string;
  meeting_url: string;
  meeting_id: string;
  join_url_expiration_datetime: string | null;
  raw_data: string | null;
  created_datetime: string;
  updated_datetime: string;
  tenant_id: number;
  calendar_event_id: number;
  tenant: { tenant_id: number; tenant_name: string; domain_name: string };
  calendar_event: {
    calendar_event_id: number;
    title: string;
    description: string;
    location: string;
    is_all_day: boolean;
    timezone: string;
    status: string;
    visibility: string;
    last_synced_at: string;
    start_datetime: string;
    end_datetime: string;
  };
}

export interface EventAttendee {
  calendar_event_attendee_id: number;
  attendee_name: string | null;
  attendee_email: string;
  attendee_status: string;
  is_organizer: boolean;
  attendee_id: string | null;
  response_datetime: string | null;
  comment: string;
  created_datetime: string;
  updated_datetime: string;
  calendar_event_id: number;
  tenant_id: number;
  tenant: { tenant_id: number; tenant_name: string; domain_name: string };
  calendar_event: {
    calendar_event_id: number;
    title: string;
    description: string;
    location: string;
    is_all_day: boolean;
    timezone: string;
    status: string;
    visibility: string;
    last_synced_at: string;
    start_datetime: string;
    end_datetime: string;
  };
}

interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const CalendarService = {
  fetchRelatedEvents: async (tenantId: string | number, eventId: number): Promise<CalendarEvent[]> => {
    const response = await axios.get<{ success: boolean; data: CalendarEvent[] }>(
      `${API_BASE_URL}/calendar-event/get-related-event/?tenant_id=${tenantId}&calendar_event_id=${eventId}`,
      { headers: getAuthHeaders() }
    );
    return response.data.data || [];
  },

  fetchEventTypes: async (tenantId: string | number): Promise<CalendarEventType[]> => {
    const response = await axios.get<PaginatedResponse<CalendarEventType>>(
      `${API_BASE_URL}/calendar-event-type/?tenant_id=${tenantId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchLinkedCalendars: async (tenantId: string | number, userId: number): Promise<LinkedCalendar[]> => {
    const response = await axios.get<PaginatedResponse<LinkedCalendar>>(
      `${API_BASE_URL}/calendar-auth-linked-calendar/?tenant_id=${tenantId}&created_by=${userId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchEvents: async (
    tenantId: string | number,
    userId: string | number,
    startGte?: string,
    startLte?: string
  ): Promise<CalendarEvent[]> => {
    let url = `${API_BASE_URL}/calendar-event/?tenant_id=${tenantId}&created_by=${userId}&is_deleted=false`;
    if (startGte) url += `&start_datetime_gte=${startGte.split("T")[0]}`;
    if (startLte) url += `&start_datetime_lte=${startLte.split("T")[0]}`;
    const response = await axios.get<PaginatedResponse<CalendarEvent>>(url, {
      headers: getAuthHeaders(),
    });
    return response.data.results || [];
  },

  fetchTasks: async (tenantId: string | number): Promise<CalendarTask[]> => {
    const response = await axios.get<PaginatedResponse<CalendarTask>>(
      `${API_BASE_URL}/calendar-task/?tenant_id=${tenantId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    const results = response.data.results || [];
    const subTasks = results.flatMap(t => (t.sub_tasks || []).filter(s => !s.is_deleted));
    return [...results, ...subTasks];
  },

  fetchTasksPaginated: async (tenantId: string | number, pageUrl?: string): Promise<{ tasks: CalendarTask[]; next: string | null; count: number }> => {
    const url = pageUrl || `${API_BASE_URL}/calendar-task/?tenant_id=${tenantId}&is_deleted=false&page_size=50`;
    const response = await axios.get<PaginatedResponse<CalendarTask>>(url, { headers: getAuthHeaders() });
    const results = response.data.results || [];
    const subTasks = results.flatMap(t => (t.sub_tasks || []).filter(s => !s.is_deleted));
    return {
      tasks: [...results, ...subTasks],
      next: response.data.next,
      count: response.data.count,
    };
  },

  fetchTaskGroups: async (tenantId: string | number, authId: number): Promise<CalendarTaskGroup[]> => {
    const response = await axios.get<PaginatedResponse<CalendarTaskGroup>>(
      `${API_BASE_URL}/calendar-task-group/?tenant_id=${tenantId}&calendar_auth_id=${authId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchCalendarAuths: async (tenantId: string | number, userId: number): Promise<any[]> => {
    const response = await axios.get<PaginatedResponse<any>>(
      `${API_BASE_URL}/calendar-auth/?tenant_id=${tenantId}&user_id=${userId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchEventMeetings: async (tenantId: string | number, eventId: number): Promise<EventMeeting[]> => {
    const response = await axios.get<PaginatedResponse<EventMeeting>>(
      `${API_BASE_URL}/calendar-event-meeting/?tenant_id=${tenantId}&calendar_event_id=${eventId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchEventAttendees: async (tenantId: string | number, eventId: number): Promise<EventAttendee[]> => {
    const response = await axios.get<PaginatedResponse<EventAttendee>>(
      `${API_BASE_URL}/calendar-event-attendee/?tenant_id=${tenantId}&calendar_event_id=${eventId}&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  fetchColorCategories: async (tenantId: string | number): Promise<any[]> => {
    const response = await axios.get<PaginatedResponse<any>>(
      `${API_BASE_URL}/calendar-color-category/?tenant_id=${tenantId}&color_type=event&is_deleted=false`,
      { headers: getAuthHeaders() }
    );
    return response.data.results || [];
  },

  createEvent: async (data: any) => {
    const headers = getAuthHeaders();
    if (data instanceof FormData) {
      delete (headers as any)["Content-Type"];
    }
    const response = await axios.post(`${MIDDLEWARE_BASE_URL}/google-calendar/event/create/`, data, {
      headers,
    });
    return response.data;
  },

  updateEvent: async (data: any) => {
    const headers = getAuthHeaders();
    if (data instanceof FormData) {
      delete (headers as any)["Content-Type"];
    }
    const response = await axios.post(`${MIDDLEWARE_BASE_URL}/google-calendar/event/update/`, data, {
      headers,
    });
    return response.data;
  },

  deleteEvent: async (calendarId: number | string, eventId: number | string) => {
    const response = await axios.post(
      `${MIDDLEWARE_BASE_URL}/google-calendar/event/delete/`,
      { calendar_id: calendarId, calendar_event_id: eventId },
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  createTask: async (data: any) => {
    const response = await axios.post(`${MIDDLEWARE_BASE_URL}/google-calendar/task/create/`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateTask: async (data: any) => {
    const response = await axios.post(`${MIDDLEWARE_BASE_URL}/google-calendar/task/update/`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  deleteTask: async (data: any) => {
    const response = await axios.post(`${MIDDLEWARE_BASE_URL}/google-calendar/task/delete/`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },
};
